import { ts } from '@shared/lib/diagTs';
import { useCallback, useEffect, useRef, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, RefreshCw, User, X } from 'lucide-react';
import { useApiClient } from '@core/api/useApiClient';
import { useRealtime } from '@core/realtime/RealtimeProvider';
import { useAccessTokenProvider } from '@core/auth/useAccessToken';
import { useCqgConnect, useCqgDisconnect, useRequestConcurrentLogin } from '../hooks/useCqgConnect';
import { abortCqgConnection, allowCqgAutoResume, blockCqgAutoResume, type CqgRelay, getCqgRelay, setCqgReconnectHandler, setCqgTokenGetter, type RelayConnState } from '../lib/cqgRelay';
import { clearStoredCqgCreds, markConnectedPopupSeen, readStoredCqgCreds, type CqgEnvironment } from '../lib/cqgCreds';
import { runCqgConnectSequence } from '../lib/cqgConnectFlow';
import { deriveBadgeDisplay, isDeadPipe, isTransportError, shouldAutoResume, type CqgBeState } from '../lib/cqgUiState';
import { latchReasonForBrokerError, readAutoResumeLatch } from '@core/realtime/autoResumeLatch';
import { getRelayOwnership } from '@core/realtime/relayOwnership';
import { useRelayOwnership } from '@core/realtime/useRelayOwnership';
import { type CqgStatus } from '../hooks/useCqgDisplayState';
import { CqgConnectedMessage } from './CqgConnectedMessage';
import { CqgLogonErrorNotice } from './CqgLogonErrorNotice';
import { useAccounts } from '@features/trades/hooks/useTradeMetadata';
import { Button, ErrorMessage, ProgressBar, SegmentedControl } from '@shared/ui';
import { BRAND_GRADIENT, gradientTextStyle } from '@shared/styles/brand';

const gradientText = gradientTextStyle;

// CQG credentials are FE-owned (localStorage, XSS caveat) — helpers live in ../lib/cqgCreds so the
// Account Sync onboarding page can stash creds without pulling this dialog into its chunk.

// Single-flight guard so the dialog instances (Topbar + banner/onboarding) auto-resume at most once
// per page load. A fresh page re-initialises this module, so it resets naturally.
let _autoResumeTried = false;

// Dead-pipe recovery (2026-08-24 prod incident): a LIVE BE session whose browser socket died later
// (a deploy roll, a network blip, laptop sleep) used to stall on "Connecting…" FOREVER — the
// page-load auto-resume above is once-per-load, and the BE pushes CqgReconnectNeeded only when IT
// lost the session, so with the session alive BE-side nobody ever retried (the BE idle watchdog
// needs 15 minutes, and its recovery rides the same dead relay). These module-level guards drive an
// exponential-backoff retry ladder (5s → 10s → 20s → 40s → 60s, then every 60s — never a storm,
// never a stall), shared across the dialog instances. The ladder resets on a real recovery and
// NEVER fires for beState 'error' (the reconnect-storm latch) or a concurrent-session conflict
// (the take-over prompt owns that decision). See isDeadPipe in ../lib/cqgUiState.
let _deadPipeAttempts = 0;
let _deadPipeRetryTimer: ReturnType<typeof setTimeout> | null = null;
export const DEAD_PIPE_RETRY_BASE_MS = 5_000;

/**
 * User-intent disconnect side-effects, shared by this dialog's Disconnect button AND the Accounts
 * panel's Unlink (2026-07-18 review: the bare POST alone lets auto-resume silently reconnect on the
 * next page load, so an Unlink would undo itself). Callers still POST via `useCqgDisconnect()`.
 */
export function forgetCqgConnection(): void {
  clearStoredCqgCreds();
  _autoResumeTried = true;
  console.log(ts(), '[cqg-auto] user disconnected - cleared stored creds, auto-resume disabled');
}

type CqgState = CqgBeState;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Row-driven open (Accounts panel): preselect this Demo/Live tab instead of the stored default. */
  environmentHint?: CqgEnvironment | null;
  /** Row-driven open: prefill this username (the FE-only per-account login memory) instead of the
   *  stored default; a differing username clears the stored password so the user re-enters it. */
  usernameHint?: string | null;
  /** Called once the dialog has consumed the hints above (so the caller can clear its one-shot hint). */
  onHintConsumed?: () => void;
}

export function CqgConnectorDialog({ open, onClose, environmentHint = null, usernameHint = null, onHintConsumed }: Props) {
  const api = useApiClient();
  const realtime = useRealtime();
  const queryClient = useQueryClient();
  const getAccessToken = useAccessTokenProvider();
  const connect = useCqgConnect();
  const disconnect = useCqgDisconnect();
  const requestConcurrentLogin = useRequestConcurrentLogin();
  // Take-over "Cancel" (keep-both) path: instead of kicking the platform, we set up a concurrent login
  // and show the 48h notice in-dialog. Never sends takeOver, so the trading-platform session stays live.
  const [showConcurrentNotice, setShowConcurrentNotice] = useState(false);
  const declineTakeover = () => {
    // Only a LIVE session needs an FCM concurrent login (a demo session can't clash with the user's
    // real trading platform), so on DEMO there is nothing to set up — just close, no misleading
    // "setting up your concurrent login" notice. On LIVE: submit the request + show the in-dialog
    // notice, and mark it seen so the app-wide CqgConnectedPopup does not also fire (2026-07-18).
    if (readStoredCqgCreds().environment !== 'live') { onClose(); return; }
    requestConcurrentLogin.mutate();
    markConnectedPopupSeen();
    setShowConcurrentNotice(true);
  };

  // Browser-relay singleton — owns the WS to wss://demoapi.cqg.com/ + the SignalR hub
  // connection to /hub/cqg. We start it lazily when the user clicks Connect to avoid an idle
  // hub connection when no CQG work is needed.
  //
  // IMPORTANT: empty deps + token-getter-via-ref. useAccessTokenProvider() returns a fresh
  // function on every render, so if we put `getAccessToken` in the deps list React reruns the
  // effect every render, tearing down + re-creating the relay (visible as a 'cqg-relay stop
  // requested' loop in the console). We want one relay per component mount.
  const relayRef = useRef<CqgRelay | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;

  // Track the ACTUAL browser→CQG socket state for this tab, so the UI reflects whether a real WS is
  // open — not just what the (possibly stale, survives-refresh) BE session reports. This is what lets
  // us always offer Connect when there's no live socket instead of getting stuck on "Connecting…".
  const [relayConn, setRelayConn] = useState<RelayConnState>('closed');
  useEffect(() => {
    setCqgTokenGetter(() => getAccessTokenRef.current());
    const relay = getCqgRelay();
    relayRef.current = relay;
    setRelayConn(relay.getConnectionState());
    const unsub = relay.onConnectionChange(() => setRelayConn(relay.getConnectionState()));
    // Start the hub on mount (not only on Connect-click) so that after a page refresh the FE
    // reconnects the hub and asks the BE to resume any still-live CQG session (CqgResume re-opens
    // the socket + re-Logon). Shared singleton + idempotent start(), so the two dialog instances
    // don't spawn duplicate relays. Resuming opens a WS only if the BE still holds a Live session;
    // for a disconnected user it's just an idle hub.
    void relay.start().catch((err) => console.error(ts(), '[cqg-dialog] relay.start() on mount failed', err));
    return () => { unsub(); }; // shared app-level singleton — do NOT stop it on unmount
  }, []);

  const status = useQuery({
    queryKey: ['cqg', 'status'],
    queryFn:  () => api.get<CqgStatus>('/api/v1/cqg/status'),
    refetchInterval: open ? 5_000 : 60_000,
  });

  // ── FE-driven auto-resume on page load ─────────────────────────────────────
  // Once we know the BE state: if there's NO live session but we DO have the user's stored creds,
  // reconnect automatically so a refresh restores the session without re-prompting. Verbose logging
  // so we can trace exactly what happens. Single-flight (module guard) so the two dialog instances
  // don't both fire it.
  useEffect(() => {
    const beState = status.data?.state;
    if (beState === undefined) return;            // wait for the first /cqg/status response
    if (_autoResumeTried) return;
    // Another CQG session (the user's trading platform) holds the slot. Do NOT auto-resume — that's the
    // logon war. The dialog shows a "take over?" prompt; reconnecting is then a deliberate user action.
    if (status.data?.reason === 'concurrent_session') {
      _autoResumeTried = true;
      console.log(ts(), '[cqg-auto] concurrent CQG session detected - not auto-resuming; user can take over from the dialog');
      return;
    }
    // A rejected logon (code 101) is terminal — do NOT auto-resume a fresh page into a guaranteed
    // re-rejection with the same stored creds. The user must Connect explicitly (which re-arms it).
    // A TRANSPORT-marked error (reason idle_timeout/transport, 2026-08-25) is NOT terminal and does
    // resume — see isTransportError.
    // A durable latch (a kick, a rejection or the user's own Disconnect, recorded by any tab — D4,
    // 2026-09-13) refuses too, whatever the BE state says by now.
    const latch = readAutoResumeLatch('cqg');
    if (!shouldAutoResume(beState, status.data?.reason ?? null, latch)) {
      _autoResumeTried = true;
      console.log('%s [cqg-auto] BE state=%s latch=%s - not auto-resuming; user must Connect manually', ts(), beState, latch ?? 'none');
      return;
    }
    _autoResumeTried = true;
    const creds = readStoredCqgCreds();
    const hasCreds = !!(creds.username && creds.password);
    console.log('%s [cqg-auto] page load: beState=%s relayConn=%s hasStoredCreds=%s', ts(), beState, relayConn, hasCreds);
    // The browser owns the socket; after a refresh it's gone while the BE may still report 'live'.
    // A live session with a CLOSED socket is a dead pipe — so resume whenever THIS tab has no socket,
    // regardless of the BE state. If the socket is already open/opening, there's nothing to resume.
    if (relayConn !== 'closed') {
      console.log('%s [cqg-auto] socket already %s (beState=%s) - no resume needed', ts(), relayConn, beState);
      return;
    }
    if (!hasCreds) {
      console.log(ts(), '[cqg-auto] no stored creds - cannot auto-resume; user must Connect manually');
      return;
    }
    // AUTO-CONNECT ON REFRESH (2026-07-08 user request — supersedes the 2026-07-05 "never
    // auto-submit" decision, which predates the credential-free contract): with stored creds
    // present, a page load silently re-establishes the session — start the relay (its CqgResume
    // renews a STILL-LIVE BE session), then fire ONE credential-less POST /cqg/connect for the
    // disconnected case; the relay logons itself from localStorage at socket-open. Safety rails
    // unchanged: single-flight per load (_autoResumeTried), terminal-rejection latch (an errored
    // state never reaches this line), and the silent Logon never sets drop_concurrent_session —
    // a takeover conflict prompts (CqgReconnectPopup) instead of kicking the platform. The
    // CqgDownBanner's grace window covers this whole handshake, so no banner flashes meanwhile.
    console.log(ts(), '[cqg-auto] socket closed - auto-connecting (credential-less POST; FE logons at socket-open)');
    void (async () => {
      // D1: a reloaded tab is the last to ask — claim the connection (the current owner yields first).
      await getRelayOwnership('cqg').claim();
      try { await relayRef.current?.start(); } catch (e) { console.error(ts(), '[cqg-auto] relay start failed', e); }
      try { await connect.mutateAsync({}); } catch (e) { console.error(ts(), '[cqg-auto] auto-connect failed', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data?.state]);

  // ── Dead-pipe retry ladder (2026-08-24 prod incident — see the module guards above) ──────────
  // Fresh mirrors for the self-scheduling timer callback: an attempt's own relay transitions
  // (closed → opening → open/closed) re-run the effect via the deps, but a start() that fails
  // without ever leaving 'closed' changes no dep — so the callback re-arms itself from these refs.
  const beStateRef = useRef<CqgBeState | undefined>(undefined);
  beStateRef.current = status.data?.state;
  const reasonRef = useRef<string | null>(null);
  reasonRef.current = status.data?.reason ?? null;
  useEffect(() => {
    const armDeadPipeRetry = () => {
      if (_deadPipeRetryTimer !== null) return; // single-flight across the dialog instances
      const delayMs = Math.min(60_000, DEAD_PIPE_RETRY_BASE_MS * 2 ** Math.min(_deadPipeAttempts, 4));
      console.log('%s [cqg-auto] dead pipe (beState=%s, socket=%s) - resuming in %ss (attempt %s)',
        ts(), beStateRef.current, relayRef.current?.getConnectionState(), delayMs / 1000, _deadPipeAttempts + 1);
      _deadPipeRetryTimer = setTimeout(() => {
        _deadPipeRetryTimer = null;
        _deadPipeAttempts++;
        void (async () => {
          await getRelayOwnership('cqg').claim();
          try { await relayRef.current?.start(); } catch (e) { console.error(ts(), '[cqg-auto] dead-pipe relay start failed', e); }
          try { await connect.mutateAsync({}); } catch (e) { console.error(ts(), '[cqg-auto] dead-pipe reconnect failed', e); }
          // Still dead after the attempt (and no dep transition to re-run the effect)? Re-arm here.
          const socketNow = relayRef.current?.getConnectionState() ?? 'closed';
          const stateNow = beStateRef.current;
          if (stateNow !== undefined && isDeadPipe(stateNow, socketNow, reasonRef.current)
              && reasonRef.current !== 'concurrent_session') {
            armDeadPipeRetry();
          }
        })();
      }, delayMs);
    };

    const state = status.data?.state;
    if (state === undefined || !_autoResumeTried) return; // the page-load resume owns the first shot
    if (!isDeadPipe(state, relayConn, status.data?.reason ?? null)) {
      // Healthy or terminal: cancel any pending retry; a real recovery re-arms the ladder from 5s.
      if (state === 'live' && relayConn === 'open' && _deadPipeAttempts > 0) {
        console.log(ts(), '[cqg-auto] pipe recovered - dead-pipe retry ladder reset');
        _deadPipeAttempts = 0;
      }
      if (_deadPipeRetryTimer !== null) { clearTimeout(_deadPipeRetryTimer); _deadPipeRetryTimer = null; }
      return;
    }
    if (status.data?.reason === 'concurrent_session') return; // take-over is the user's call, never auto
    if (readAutoResumeLatch('cqg') !== null) return; // a kick / rejection / Disconnect is latched — never auto
    if (getRelayOwnership('cqg').isPassive()) return; // another tab owns the socket — its pipe, not ours (D1)
    const creds = readStoredCqgCreds();
    if (!creds.username || !creds.password) return; // nothing to logon with at socket-open
    if (connect.isPending) return;
    armDeadPipeRetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data?.state, status.data?.reason, relayConn, connect.isPending]);

  // ── Silent re-establish when the BE signals it lost our session (CqgReconnectNeeded) ─────────
  // The BE drops relayed CQG frames when it has no session for us (e.g. a pod roll mid-session),
  // which loses fills and creates CQG↔FIFO mismatches. Decision 2026-07-08: blips auto-reconnect
  // SILENTLY — ensure the relay hub is up, then POST a CREDENTIAL-LESS /cqg/connect; the BE re-arms
  // and pushes CqgOpenSocket, and the relay logons itself from stored creds at socket-open. The
  // silent Logon never sets drop_concurrent_session, so a take-over conflict surfaces the prompt
  // (CqgReconnectPopup) instead of kicking the user's platform. No stored creds ⇒ nothing to logon
  // with ⇒ stay quiet and let the user Connect. Single-flight via connect.isPending; the hub-side
  // 3s debounce + the terminal-rejection latch still gate the flood.
  const resumeWithStoredCreds = useCallback(async () => {
    const creds = readStoredCqgCreds();
    if (!creds.username || !creds.password) {
      console.log(ts(), '[cqg-reconnect] BE lost our session - no stored creds; awaiting user Connect');
      return;
    }
    if (connect.isPending) return;
    console.log(ts(), '[cqg-reconnect] BE lost our session - silently re-establishing (credential-less connect; relay logons at socket-open)');
    await getRelayOwnership('cqg').claim();
    try { await relayRef.current?.start(); } catch (e) { console.error(ts(), '[cqg-reconnect] relay start failed', e); }
    try { await connect.mutateAsync({}); } catch (e) { console.error(ts(), '[cqg-reconnect] silent connect failed', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connect.isPending]);
  const resumeRef = useRef(resumeWithStoredCreds);
  resumeRef.current = resumeWithStoredCreds;
  useEffect(() => {
    // Stable registration (calls the latest resume via the ref). Last-mounted dialog instance wins; its
    // single-flight guard ensures one re-logon per BE request even with the 3 dialog instances mounted.
    setCqgReconnectHandler(() => { void resumeRef.current(); });
  }, []);

  // Persisted creds (FE-owned) — see readStoredCqgCreds / CREDS_STORAGE_KEY at module scope.
  const initialCreds = readStoredCqgCreds();
  const [username, setUsername] = useState(initialCreds.username);
  const [password, setPassword] = useState(initialCreds.password);
  // DEMO | LIVE gateway pair (2026-07-18 selector) — persisted with the creds, sent as the connect body.
  const [environment, setEnvironment] = useState<CqgEnvironment>(initialCreds.environment);
  const [relayStarting, setRelayStarting] = useState(false);

  // Re-seed the form from storage EVERY time the dialog opens (2026-07-18 review): this instance is
  // mounted at page load, so its state can be stale — e.g. the Add-account dialog just stored fresh
  // creds/env and handed off a take-over here; connecting with the mount-time state would clobber them.
  // Row-driven opens (Accounts panel) pass environment/username hints that WIN over the stored default
  // so Connect on a specific account preselects its tab + prefills the username last used for it; a
  // hint username that differs from the stored one clears the password so the user re-enters it.
  useEffect(() => {
    if (!open) return;
    const stored = readStoredCqgCreds();
    const username = usernameHint ?? stored.username;
    setUsername(username);
    setPassword(usernameHint && usernameHint !== stored.username ? '' : stored.password);
    setEnvironment(environmentHint ?? stored.environment);
    if (environmentHint || usernameHint) onHintConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // A user-initiated connect shows the "Importing your trades" progress view (stepped + crawl) until
  // the BE signals cqg.import.complete. (Replaces the old "auto-close on live" behaviour — we now
  // keep the dialog up to show import progress and close ~700ms after it reaches 100%.)
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);

  // Write each cqg.state push into the SHARED ['cqg','status'] cache (same key the poll + topbar badge
  // use), so the badge and dialog are ONE source of truth and can't disagree on open. (Previously the
  // dialog kept a private pushedState it dropped on open → it fell back to a stale 5s poll and flashed
  // the login form while the badge was already LIVE.)
  useEffect(() => {
    return realtime.subscribe('cqg.state', (payload) => {
      const next = (payload.state ?? '').toLowerCase() as CqgState;
      const err = (payload as { lastError?: string | null }).lastError ?? null;
      console.log('%s [cqg-dialog] realtime cqg.state push → %s%s', ts(), next, err ? ` (err=${err})` : '');
      // A rejected/failed logon (code 101, etc.) is terminal — latch off all auto-resume so we don't
      // hammer CQG with the same bad creds on every SignalR blip. Latch on THIS 'error' push (not by
      // reading the cache later): the BE follows 'error' with 'disconnected' within a frame, so a
      // deferred cache read would miss it. Cleared only by an explicit user Connect (doConnect).
      // A terminal logon error also clears the onboarding "route to connected screen" marker (B), so a
      // failed onboarding auto-connect never leaves it set to misroute a later manual connect.
      // Durable (every tab + reload) for a rejection or a kick (reason kicked/concurrent_session) since
      // 2026-09-13 (D4); a TRANSPORT error keeps latching this tab only so a reload still self-heals.
      if (next === 'error') {
        const reason = payload.reason ?? null;
        blockCqgAutoResume(isTransportError(next, reason) ? null : latchReasonForBrokerError(reason));
        getRelayOwnership('cqg').release(); // a refused / kicked / dead owner is no owner (D1)
      }
      queryClient.setQueryData<CqgStatus>(['cqg', 'status'], (prev) => ({
        state: next,
        accounts: payload.accounts ?? prev?.accounts ?? [],
        lastError: err,
        reason: payload.reason ?? null,
      }));
    });
  }, [realtime, queryClient]);

  // On open, refetch the status so a dialog that was closed for a while (60s poll cadence) shows fresh
  // state immediately. State now comes solely from the shared ['cqg','status'] cache (poll + push), so
  // the dialog always matches the badge — there's no private pushedState to drop.
  useEffect(() => {
    if (open) void status.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const state: CqgState = ((status.data?.state ?? 'disconnected') as string).toLowerCase() as CqgState;
  const lastError = status.data?.lastError ?? null;
  const reason = status.data?.reason ?? null;
  // Another CQG session (the user's trading platform) is active — show the take-over prompt instead of
  // the login form, and never auto-reconnect (no logon war).
  const isConcurrent = reason === 'concurrent_session';
  const accounts = status.data?.accounts ?? [];
  // Join the session's account ids with the accounts registry so the Connected view can show the
  // same friendly data as the Accounts panel (display name + brokerage number), not just the raw id.
  const allAccounts = useAccounts();
  const accountsById = new Map((allAccounts.data ?? []).map(a => [a.name, a]));
  const isLive = state === 'live';
  // Derive the dialog's view from the SAME function + inputs as the topbar badge —
  // deriveBadgeDisplay(state, relayConn) (unit-tested in cqgUiState.ts) — so the icon and the window are
  // ALWAYS on the same page: live → Connected view; connecting → Connecting view; error/disconnected →
  // login form. There is no separate "session-only" predicate that could disagree with the badge.
  const ownership = useRelayOwnership('cqg');
  const passive = ownership.mode === 'passive';
  const display = deriveBadgeDisplay(state, relayConn, reason, passive);
  const showConnected = display === 'live';
  const showConnecting = display === 'connecting';
  // "Connecting…" reflects an in-flight attempt FROM THIS TAB only — never a stale BE state, which
  // would otherwise disable the Connect button forever after a refresh.
  const isConnecting = connect.isPending || relayStarting || relayConn === 'opening';

  // Trace which view renders + the inputs to it (helps diagnose "login form shown while connected").
  useEffect(() => {
    if (!open) return;
    const view = importing ? 'importing' : isConcurrent ? 'take-over' : showConnected ? 'connected' : showConnecting ? 'connecting' : 'connect-form';
    console.log('%s [cqg-dialog] view=%s (state=%s, socket=%s, isLive=%s, isConnecting=%s, reason=%s)', ts(), view, state, relayConn, isLive, isConnecting, reason);
  }, [open, importing, isConcurrent, showConnected, showConnecting, state, relayConn, isLive, isConnecting, reason]);

  // ── Import-progress driver (stepped + crawl) ───────────────────────────────
  // CQG never reports a total, so the bar advances through real phases: connecting → logged-in →
  // each imported batch nudges it up → 100% on the authoritative "snapshot delivered" signal.
  //
  // Completion fires from whichever arrives first:
  //  (1) PRIMARY — the relay's own `TradeSnapshotCompletions` socket frame. Same channel the import
  //      flows on, so it can't be missed.
  //  (2) BACKUP — the `/hub/notifications` `cqg.import.complete` event. This can be missed when that
  //      hub isn't subscribed yet at publish time (the BE finishes the snapshot ~1s after logon),
  //      which is exactly what left the bar frozen at 55%.
  const finishImport = useCallback((source: string) => {
    console.log('%s [cqg-dialog] import COMPLETE via %s → 100%%, closing in 700ms', ts(), source);
    setProgress(100);
    window.setTimeout(() => { setImporting(false); setProgress(0); onClose(); }, 700);
  }, [onClose]);

  useEffect(() => {
    if (!importing) return;
    if (relayConn === 'opening' || relayConn === 'open') {
      console.log('%s [cqg-dialog] phase: relay socket=%s → progress≥25', ts(), relayConn);
      setProgress(p => Math.max(p, 25));
    }
  }, [importing, relayConn]);

  useEffect(() => {
    if (!importing) return;
    if (state === 'live')  { console.log(ts(), '[cqg-dialog] phase: state=live → progress≥55'); setProgress(p => Math.max(p, 55)); }
    if (state === 'error') { console.warn(ts(), '[cqg-dialog] phase: state=error → back to form'); setImporting(false); setProgress(0); }
  }, [importing, state]);

  // PRIMARY progress + completion: react to CQG frames on the relay's OWN socket (already connected
  // during import). Snapshot completion finishes the bar; history/order batches crawl it toward 92.
  useEffect(() => {
    if (!importing) return;
    const relay = relayRef.current;
    if (!relay) return;
    console.log(ts(), '[cqg-dialog] subscribed to relay frames for import progress');
    return relay.onMessage((summary) => {
      if (summary.includes('TradeSnapshotCompletions')) {
        finishImport('relay frame TradeSnapshotCompletions');
      } else if (summary.includes('InformationReports') || summary.includes('OrderStatuses')) {
        setProgress(p => {
          const n = Math.min(92, p + Math.max(2, (92 - p) * 0.12));
          console.log('%s [cqg-dialog] relay frame %s → progress=%d', ts(), summary, Math.round(n));
          return n;
        });
      }
    });
  }, [importing, finishImport]);

  // BACKUP progress + completion: notifications-hub events (kept for resilience).
  useEffect(() => {
    if (!importing) return;
    return realtime.subscribe('trades.changed', () => {
      setProgress(p => {
        const n = Math.min(92, p + Math.max(2, (92 - p) * 0.15));
        console.log('%s [cqg-dialog] realtime trades.changed → progress=%d', ts(), Math.round(n));
        return n;
      });
    });
  }, [importing, realtime]);

  useEffect(() => {
    if (!importing) return;
    return realtime.subscribe('cqg.import.complete', () => finishImport('realtime cqg.import.complete'));
  }, [importing, realtime, finishImport]);

  // Safety net: if NO completion signal arrives within 30s, stop blocking on the progress view (the
  // session is live by then — reopening shows the connected view). The import is normally ~1-2s, so a
  // long spinner means a missed completion signal, not a slow import.
  useEffect(() => {
    if (!importing) return;
    const t = window.setTimeout(() => {
      console.warn(ts(), '[cqg-dialog] import progress timeout (30s) with no completion signal - dropping to connected view');
      setImporting(false);
    }, 30_000);
    return () => window.clearTimeout(t);
  }, [importing]);

  // Escape-to-close (the bespoke modal replaces the shared Dialog, which provided this).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Shared connect path for the login form (takeOver=false) and the take-over prompt (takeOver=true).
  // takeOver arms the one-shot drop_concurrent_session on the NEXT FE-built Logon so CQG drops the
  // user's OTHER session. Credentials go ONLY to localStorage — the POST body carries just the
  // non-secret `environment` (2026-07-08: CQG creds are forbidden from transiting our BE; the relay
  // logons itself at socket-open). The sequence itself lives in `runCqgConnectSequence` so the
  // Add-account dialog runs the exact same safety steps (auto-resume re-arm, relay-first, popup arm).
  const doConnect = async (takeOver: boolean) => {
    // Switch to the "Importing your trades" progress view immediately; the bar climbs as the
    // connect → logon → import phases land (see the progress-driver effects above).
    setImporting(true);
    setProgress(8);
    setRelayStarting(true);
    try {
      await runCqgConnectSequence({
        // Take-over connects NEVER re-store: the take-over view has no form fields, and re-storing
        // this component's state would clobber creds/env freshly stored by the Add-account dialog /
        // onboarding handoff (2026-07-18 review; see also the re-seed-on-open effect above).
        creds: takeOver ? null : { username, password, environment },
        takeOver,
        mutate: (vars) => connect.mutateAsync(vars),
      });
    } catch (err) {
      // The POST failed (rate-limit 429, network, 5xx) — nothing is connecting, so drop the progress
      // view immediately instead of letting it hang until the 30s safety timeout; the form re-renders
      // with connect.isError → <ErrorMessage>. (2026-07-18 review: a 429 previously stuck the view.)
      console.error(ts(), '[cqg-dialog] connect POST failed', err);
      setImporting(false);
      setProgress(0);
    } finally {
      setRelayStarting(false);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await doConnect(false);
  };

  // "Refresh" from the CONNECTED view (2026-07-09 review): re-arm the session — relay hub up, then
  // a credential-less /cqg/connect; the BE pushes cqg.openSocket and the relay re-Logons from the
  // creds ALREADY in localStorage + re-pulls history. Deliberately NOT doConnect: that would
  // storeCqgCreds(username, password) with this view's EMPTY form fields and clobber the stored
  // creds; and it never takes over (no logon war). Failures fall through to the existing
  // error/connect-form machinery.
  const refresh = async () => {
    allowCqgAutoResume();
    try {
      await relayRef.current?.start();
    } catch (err) {
      console.error(ts(), '[cqg-dialog] refresh: relay.start() failed', err);
    }
    await connect.mutateAsync({});
  };

  // Cancel from the importing view = dismiss the progress + close the dialog. The connection/import
  // keep running in the background (a successful logon shouldn't be torn down); reopening shows the
  // connected view (with Disconnect) once import finishes.
  const cancelImporting = () => {
    setImporting(false);
    setProgress(0);
    onClose();
  };

  // Cancel from the CONNECTING/resuming view = actually ABORT the not-yet-live attempt (the old
  // behaviour just closed the window while the connect kept running in the background). Drop the BE
  // session AND stop this tab's relay so the socket doesn't keep re-opening. The dialog STAYS OPEN
  // (user direction 2026-07-18): the abort drives the state to disconnected, which re-renders this
  // dialog as the credentials form — Cancel returns you to the login screen, not out of the flow.
  // Distinct from cancelImporting, which leaves a successful LIVE session running.
  const cancelConnecting = useCallback(() => {
    setImporting(false);
    setProgress(0);
    void abortCqgConnection({
      disconnect: () => disconnect.mutateAsync(),
      stopRelay: () => relayRef.current?.stop() ?? Promise.resolve(),
    });
  }, [disconnect]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'var(--scrim)' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Connect your CQG"
        onClick={e => e.stopPropagation()}
        className="flex w-full max-w-[564px] flex-col gap-4 rounded-2xl bg-canvas-raised p-6 shadow-card"
        style={{ border: '1px solid var(--canvas-divider)' }}
      >
        {/* Header — title + bordered close button (matches Figma). During the connect→import flow the
            title tracks the PHASE: "Connecting to your CQG" until the session is live, then "Importing
            your trades" — so it never says "connecting" once the badge is already LIVE (the connection
            is done; the progress bar is now import progress). The X is hidden while importing (the
            Figma hides it — use Cancel instead). */}
        <header className="flex items-center justify-between gap-2.5">
          <h2 className="flex items-center gap-2 text-base font-bold text-text">
            <span aria-hidden>🔌</span> {importing ? (isLive ? 'Importing your trades' : 'Connecting to your CQG') : isConcurrent ? 'CQG session in use' : showConnecting ? 'Connecting to your CQG' : showConnected ? (passive ? 'Connected to CQG in another tab' : 'Currently connected to CQG') : 'Connect your CQG'}
          </h2>
          {!importing && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-lg border bg-canvas-raised text-text-muted transition-colors hover:text-text"
              style={{ borderColor: 'var(--canvas-divider)' }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </header>

        {showConcurrentNotice ? (
          /* ── Take-over declined → setting up a concurrent login (48h notice) ── */
          <ConcurrentNoticeView onClose={() => { setShowConcurrentNotice(false); onClose(); }} />
        ) : importing ? (
          /* ── Importing progress (Figma) ────────────────────────────────── */
          <ImportingView progress={progress} isLive={isLive} onCancel={cancelImporting} />
        ) : isConcurrent ? (
          /* ── Another CQG session active → ask before taking over (no auto-war) ── */
          <TakeOverView lastError={lastError} pending={isConnecting} onTakeOver={() => doConnect(true)} onCancel={declineTakeover} />
        ) : !showConnected ? (
          showConnecting ? (
            /* ── Connecting / resuming — mirrors the badge's "connecting" so icon + window agree ── */
            <ConnectingView onCancel={cancelConnecting} />
          ) : (
          /* ── Connection dialog ─────────────────────────────────────────── */
          <form onSubmit={submit} id="cqg-connect-form" className="flex flex-col gap-4">
            <p className="text-[13px] leading-5 text-text-muted">
              Link your CQG account to sync your trades automatically.
            </p>

            {/* LIVE | DEMO gateway pair (2026-07-18 selector; LIVE first + the default since
                2026-08-21, user decision — real traders must never land on the sandbox, where their
                real creds 101-reject and look like a bad password). Live = api.cqg.com (real trading
                accounts); Demo = demoapi.cqg.com sandbox (demo creds only). The BE resolves the
                matching app-id pair and pushes it with CqgOpenSocket — a mismatched pair 101-rejects.
                A STORED demo choice still reseeds the tab on open: the environment state is
                load-bearing (the concurrent-login flow must never fire for a demo session), so only
                the fresh-user default and the segment order are Live-first. */}
            <SegmentedControl
              ariaLabel="CQG environment"
              size="sm"
              value={environment}
              onChange={setEnvironment}
              options={[
                { value: 'live', label: 'Live' },
                { value: 'demo', label: 'Demo' },
              ]}
            />

            {/* CQG Logon credentials are a Username + Password (not an email / one-time code). */}
            <CqgInput
              icon={<User className="h-5 w-5" />}
              type="text"
              value={username}
              onChange={setUsername}
              placeholder="Username"
              autoComplete="username"
              autoFocus
              required
            />
            <CqgInput
              icon={<KeyRound className="h-5 w-5" />}
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="Password"
              autoComplete="current-password"
              required
            />

            {connect.isError && <ErrorMessage error={connect.error} />}
            {/* Keep the last CQG rejection visible on the form. The BE follows an 'error' push with
                'disconnected' within a frame (and a failed auto-retry cycles error→connecting→
                disconnected), so gating on state==='error' made the message vanish and dropped the
                user back to a BLANK form. Show it whenever a lastError is set and we're not mid-attempt
                (the form only renders for error/disconnected, so this can't cover the connecting view). */}
            {/* A rejection that carries a link to CQG's OWN domain (e.g. the End User License
                Agreement refusal, which arrives as the GENERIC code 101) is reframed by
                CqgLogonErrorNotice into our own copy + a real button; every other message renders
                byte-for-byte as it always has. */}
            {lastError && !isConnecting && (
              <CqgLogonErrorNotice message={lastError} variant="form" />
            )}

            <p className="text-[13px] leading-5 text-text-muted">
              Don&apos;t have a CQG account? Contact Optimus Futures at{' '}
              {/* mailto: opens the user's mail client (the old /contact page 404'd). */}
              <a
                href="mailto:support@optimusfutures.com"
                className="font-semibold"
                style={gradientText}
              >
                support@optimusfutures.com
              </a>
            </p>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
                style={gradientText}
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isConnecting}
                className="rounded-lg px-3 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND_GRADIENT }}
              >
                {isConnecting ? 'Connecting…' : 'Connect'}
              </button>
            </div>
          </form>
          )
        ) : (
          /* ── Connected state (2026-07-09 review: "Currently connected to CQG" title, the CQG
                account(s) it is connected to, a Refresh reconnect, Close + Disconnect. The manual
                "Import historical orders" button was REMOVED — history is pulled automatically on
                every logon, so the button was redundant.) ──────────── */
          <div className="flex flex-col gap-4">
            {accounts.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted">
                  {accounts.length === 1 ? 'Account' : 'Accounts'}
                </div>
                {/* Join the session's account ids with the accounts registry so the connected view
                    shows the SAME friendly data as the Accounts panel row: display name (e.g.
                    "SIMcqgxl"), the brokerage account number (e.g. "PSCQGXL"), and the internal
                    account number. Falls back to the raw id if the registry hasn't caught up yet. */}
                <ul className="mt-1 space-y-2">
                  {accounts.map(id => {
                    const info = accountsById.get(id);
                    return (
                      <li key={id} className="flex flex-col">
                        <span className="text-sm font-semibold text-text">{info?.displayName || id}</span>
                        <span className="text-xs uppercase tracking-wide text-text-muted">
                          {info?.brokerageAccountNumber ? `ID: ${info.brokerageAccountNumber} · ` : ''}No. {id}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <Button
              variant="secondary"
              fullWidth
              loading={connect.isPending}
              onClick={() => void refresh()}
              icon={<RefreshCw className="h-4 w-4" />}
            >
              {connect.isPending ? 'Refreshing…' : 'Refresh'}
            </Button>

            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
                style={gradientText}
              >
                Close
              </button>
              <button
                type="button"
                disabled={disconnect.isPending}
                onClick={() => {
                  // Explicit disconnect = a real logoff: forget the stored creds and block
                  // auto-resume so we don't silently reconnect on the next page load. Shared with
                  // the Accounts panel's Unlink (forgetCqgConnection).
                  forgetCqgConnection();
                  disconnect.mutate();
                }}
                className="rounded-lg px-3 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                style={{ background: BRAND_GRADIENT }}
              >
                {disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

/**
 * "Importing your trades" progress view (Figma ModalCQG) — shown while a user-initiated connect/import
 * is in flight: illustration, message, brand-gradient progress bar + %, and a Cancel button.
 */
function ImportingView({ progress, isLive, onCancel }: { progress: number; isLive: boolean; onCancel: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="flex w-full flex-col items-center gap-4 py-2">
        <ImportingIllustration />
        <p className="text-center text-[13px] font-semibold leading-4 text-text">
          {isLive
            ? <>Importing your trades,<br />this may take a few minutes</>
            : <>Connecting to your CQG…</>}
        </p>
        <div className="flex w-full max-w-[238px] flex-col items-center gap-2">
          <ProgressBar value={progress} />
          <span className="text-[13px] leading-5 text-text-muted">{Math.round(progress)}%</span>
        </div>
      </div>
      <div className="flex w-full items-center justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
          style={gradientText}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * Take-over prompt — shown when CQG reports a concurrent session (the user's other CQG app, usually
 * their trading platform, holds the single allowed session). We ask before taking over instead of
 * silently kicking the other session (or auto-warring with it). "Take over" re-connects with
 * drop_concurrent_session=true so CQG drops the other session and the journal wins.
 */
function TakeOverView({ lastError, pending, onTakeOver, onCancel }: { lastError: string | null; pending: boolean; onTakeOver: () => void; onCancel: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-3 py-2 text-center">
        <span className="text-3xl" aria-hidden>⚠️</span>
        <p className="text-[13px] font-semibold leading-5 text-text">You&apos;re already signed in to CQG</p>
        <p className="text-[13px] leading-5 text-text-muted">
          Your CQG login is already in use (most likely your trading platform). CQG allows only one
          session at a time, so continuing here will log you out of the platform and take over the screen.
          Cancel to keep the platform running and set up a concurrent login instead.
        </p>
        {/* Same treatment as the form's error line, so the take-over view can't keep a dead-end
            version of a broker message that points at a page the trader has to open. */}
        {lastError && <CqgLogonErrorNotice message={lastError} variant="takeover" />}
      </div>
      <div className="flex items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
          style={gradientText}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={pending}
          onClick={onTakeOver}
          className="rounded-lg px-3 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          style={{ background: BRAND_GRADIENT }}
        >
          {pending ? 'Taking over…' : 'Take over here'}
        </button>
      </div>
    </div>
  );
}

/**
 * Shown after the user CANCELS the take-over: we keep their trading-platform session live and set up a
 * concurrent login instead (the request was just submitted). Same 48-hour copy as the in-app connected
 * popup and the onboarding connected screen (shared <see cref="CqgConnectedMessage" />).
 */
function ConcurrentNoticeView({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-[17px] font-bold text-text">Setting up your concurrent login</h2>
      <CqgConnectedMessage />
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-4 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
          style={{ background: BRAND_GRADIENT }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

/**
 * "Connecting…" view — shown when the badge derivation (deriveBadgeDisplay) reads 'connecting' and no
 * user-initiated import is in flight (e.g. a refresh/auto-resume re-opening the socket). Mirrors the
 * topbar badge so the icon and the window always agree: icon connecting → window connecting.
 */
function ConnectingView({ onCancel }: { onCancel: () => void }) {
  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex w-full flex-col items-center gap-4 py-2">
        <ImportingIllustration />
        <p className="text-center text-[13px] font-semibold leading-4 text-text">Connecting to your CQG…</p>
        <p className="text-center text-[13px] leading-5 text-text-muted">Re-establishing the live connection.</p>
        <div className="h-1 w-full max-w-[238px] overflow-hidden rounded-full bg-canvas-inset">
          <div className="h-full w-full animate-pulse rounded-full" style={{ background: BRAND_GRADIENT }} />
        </div>
      </div>
      <div className="flex w-full items-center justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-canvas-inset px-3 py-2 text-[13px] font-semibold transition-opacity hover:opacity-80"
          style={gradientText}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * ID-card illustration for the importing view. PLACEHOLDER approximation of the Figma graphic using
 * its palette (#D5DEF2 / #B2BEDB / #ECF1FC) — swap in the exact exported SVG when provided.
 */
function ImportingIllustration() {
  return (
    <svg width="138" height="120" viewBox="0 0 138 120" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden role="img">
      <ellipse cx="69" cy="64" rx="58" ry="44" fill="#D5DEF2" opacity="0.55" />
      <rect x="34" y="22" width="70" height="74" rx="8" fill="#FFFFFF" stroke="#B2BEDB" strokeWidth="1.3" />
      <circle cx="69" cy="42" r="11" fill="#ECF1FC" stroke="#B2BEDB" strokeWidth="1.3" />
      <circle cx="69" cy="38.5" r="3.6" fill="#B2BEDB" />
      <path d="M61.5 50c0-3.6 3.4-5.6 7.5-5.6s7.5 2 7.5 5.6" fill="#B2BEDB" />
      <rect x="46" y="62" width="46" height="6" rx="3" fill="#ECF1FC" />
      <rect x="46" y="74" width="46" height="6" rx="3" fill="#ECF1FC" />
      <rect x="58" y="86" width="22" height="6" rx="3" fill="#D5DEF2" />
      <path d="M112 30v8M108 34h8" stroke="#B2BEDB" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Bordered input pill with a leading icon, matching the Figma Text-Input (48px tall, 1.5px
 * border, 12px radius). Border + text colours come from theme tokens so it works in dark/light;
 * focus lifts the border to the accent colour.
 */
function CqgInput({
  icon, value, onChange, type, ...rest
}: {
  icon: ReactNode;
  value: string;
  onChange: (v: string) => void;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  // Password fields get a trailing show/hide eye toggle (2026-07-18 user direction).
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  return (
    <label className="flex h-12 items-center gap-3 rounded-xl border-[1.5px] border-[color:var(--canvas-divider)] bg-canvas-raised px-3 transition-colors focus-within:border-[color:var(--accent)]">
      <span className="text-text-muted">{icon}</span>
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        type={isPassword && revealed ? 'text' : type}
        className="w-full bg-transparent text-[13px] text-text outline-none placeholder:text-text-muted"
        {...rest}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setRevealed(r => !r)}
          aria-label={revealed ? 'Hide password' : 'Show password'}
          className="text-text-muted transition-colors hover:text-text"
        >
          {revealed ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      )}
    </label>
  );
}
